import AddDealForm from '../deal/add_deal';
import AddPipelineModal from './modal/add_pipeline';
import AddStageModal from '../stage/modal/add_stage';
import { Box } from '@mui/material';
import { getAllPipelinesThunk } from '../../redux/thunks/pipeline';
import PipelineContent from './pipeline_content';
import PipelineHeader from './pipeline_header';
import PipelineSkeleton from './pipeline_skeleton';
import RenderEmptyState from './render_empty_state';
import SelectAllComponent from './select_all';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: Pipeline Component
 * @paragraph: This is the main component for managing pipelines, including viewing pipelines, adding stages, and performing pipeline-related actions.
 * @heading4: PipelineContainer
 * @paragraph: A styled Box component that serves as the container for the entire pipeline interface.
 */
const PipelineContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    border: theme.palette.mode === 'dark' ? '1px solid rgb(94, 94, 94)' : '1px solid #f4f4f4',
    backgroundColor: theme.palette.mode === 'dark' ? 'black' : '#ffffff',
    borderRadius: '8px'
}));

/**
 * @heading1: PipelineComponent
 * @paragraph: The main functional component for rendering and managing pipelines.
 */
const PipelineComponent: React.FC = () => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
     * showPopup/addStage: React states to store the values for modal.
     * pipelines/selectedPipeline/isAddingDeal/loaders: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const pipelineRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [addStage, setAddStage] = useState<boolean>(false);
    const { pipelines, selectedPipeline, loaders, bulkEditMode } = useSelector((state: RootState) => state.pipeline);
    const { flags } = useSelector((state: RootState) => state.deal);
    const disabled = loaders.isCreating || loaders.isDeleting || loaders.isLoading || loaders.isUpdating;

    /**
     * @heading3: Functions
     * @heading4: useEffect
     * @paragraph: Fetches all pipelines on component mount.
     */
    useEffect(() => {
        const fetchAllPipelines = async () => {
            try {
                await dispatch(getAllPipelinesThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
            } catch (e) {
                // Catch errors
            }
        };
        fetchAllPipelines();
    }, [dispatch]);

    const scrollPipeline = (direction: 'left' | 'right') => {
        if (pipelineRef.current) {
            const scrollAmount = 250;
            pipelineRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const updateScrollButtons = () => {
        if (pipelineRef.current) {
            setCanScrollLeft(pipelineRef.current.scrollLeft > 0);
            setCanScrollRight(pipelineRef.current.scrollLeft + pipelineRef.current.clientWidth < pipelineRef.current.scrollWidth);
        }
    };

    useEffect(() => {
        updateScrollButtons();
        if (pipelineRef.current) {
            pipelineRef.current.addEventListener('scroll', updateScrollButtons);
        }
        window.addEventListener('resize', updateScrollButtons);
        return () => {
            if (pipelineRef.current) {
                pipelineRef.current.removeEventListener('scroll', updateScrollButtons);
            }
            window.removeEventListener('resize', updateScrollButtons);
        };
    }, []);

    /**
     * @returns: {JSXElement} - The rendered pipeline component.
     */
    if (flags.isAddingDeal) {
        return <AddDealForm />;
    }
    return (
        <PipelineContainer>
            {/* Pipeline Header with actions */}
            <PipelineHeader setShowPopup={setShowPopup} scrollPipeline={scrollPipeline}
                canScrollLeft={canScrollLeft}
                canScrollRight={canScrollRight} setAddStage={setAddStage}/>
            {/* Pipeline Action Dropdown */}
            {/* {selectedPipeline && !bulkEditMode && <PipelineActionDropdown />} */}
            {selectedPipeline && bulkEditMode && <SelectAllComponent />}
            {/* Show skeleton loader if loading */}
            {loaders.isLoading && <PipelineSkeleton />}
            {/* Show empty state if no pipelines are available */}
            {!loaders.isLoading && pipelines.length === 0 && <RenderEmptyState isPipeline={true} disabled={disabled} onSubmit={() => setShowPopup(true)} />}
            {/* Pipeline Content */}
            <PipelineContent setAddStage={setAddStage} pipelineRef={pipelineRef}/>
            {/* Add Pipeline Modal */}
            <AddPipelineModal open={showPopup} onClose={() => setShowPopup(false)} />
            {/* Add Stage Modal */}
            <AddStageModal open={addStage} onClose={() => setAddStage(false)} />
        </PipelineContainer>
    );
};

export default PipelineComponent;
